import {makeAutoObservable, reaction, runInAction} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {
  Destructible,
  WithReactionDispose,
} from '../../../../../utils/store';
import type {Nullable} from '../../../../../utils/utility-types';
import type {
  Edition,
  Program,
  ScheduledConference,
  ScheduledTask,
  ScheduleEvent,
  ScheduleItem,
  Section,
} from '../types';

import {editionStore} from '../../../../../api/program';
import {alert} from '../../../../../components/alert';
import {
  EVERGREEN_PROGRAM_SCHEDULE_DURATION,
  isEditionEvergreen,
  ProgramScheduleItemTypeEnum,
} from '../../../../../utils/entity/program';
import {getUuid} from '../../../../../utils/getUuid';
import {ReactionDisposeManager} from '../../../../../utils/store';
import {groupEditionConferences, repeatScheduleEvents} from './utils';

export class CRUProgramScheduleStore
  implements Destructible, WithReactionDispose
{
  conferences: ScheduledConference[] = [];
  disposer = new ReactionDisposeManager();
  isIndividual = false;
  program?: Nullable<Program>;
  tasks: ScheduledTask[] = [];

  constructor(program?: Nullable<Program>) {
    this.program = program;

    makeAutoObservable(this, {}, {autoBind: true});

    this.disposer.watch(
      'program',
      reaction(
        () => this.program,
        rProgram => {
          if (rProgram) {
            const lastEdition = rProgram.editions.slice().pop();

            if (lastEdition) {
              this.conferences = groupEditionConferences(
                lastEdition.conferences || [],
              );
              this.tasks = (lastEdition.tasks || []).map(item => ({
                ...item,
                edition_uuid: getUuid(),
              }));

              this.isIndividual = lastEdition.group_size === 1;
            }
          }
        },
      ),
    );

    this.disposer.watch(
      'edition.update',
      reaction(
        () => editionStore.updating,
        updating => {
          if (
            program &&
            updating.success &&
            updating.entity &&
            updating.entity._id === program._id
          ) {
            if (this.lastEdition) {
              this.conferences = groupEditionConferences(
                this.lastEdition.conferences || [],
              );
              this.tasks = (this.lastEdition.tasks || []).map(item => ({
                ...item,
                edition_uuid: getUuid(),
              }));
            }
          }
        },
      ),
    );
  }

  destruct() {
    this.disposer.dispose();
  }

  get isEmpty() {
    return !this.schedule.length;
  }

  get lastEdition() {
    return this.program?.editions.slice().pop();
  }

  get programDuration() {
    return isEditionEvergreen(this.lastEdition)
      ? EVERGREEN_PROGRAM_SCHEDULE_DURATION
      : this.lastEdition?.duration || 1;
  }

  get schedule() {
    const duration = this.programDuration;

    const data = [
      ...repeatScheduleEvents(
        this.isIndividual ? [] : this.conferences || [],
        duration,
      )
        .filter(conference =>
          this.isScheduleItemInDurationRange(conference as ScheduleItem),
        )
        .map(item => ({
          ...item,
          type: ProgramScheduleItemTypeEnum.CONFERENCE,
        })),
      ...repeatScheduleEvents(this.tasks || [], duration)
        .filter(this.isScheduleItemInDurationRange)
        .map(item => ({
          ...item,
          type: ProgramScheduleItemTypeEnum.TASK,
        })),
    ];

    return data;
  }

  get sections() {
    const sections: Section[] = [];
    const groups: {[key: number]: ScheduleItem[]} = {};

    const groupByDays = (item: ScheduleItem) => {
      const groupId = item.day;

      groups[groupId] = groups[groupId] || [];
      groups[groupId].push(item);
    };

    this.schedule.forEach(groupByDays);

    Object.keys(groups)
      // @ts-expect-error: TODO: describe why
      .sort((a, b) => a - b)
      .forEach(key => {
        const day = parseInt(key, 10);

        sections.push({
          data: groups[key].sort(
            (a: ScheduleItem, b: ScheduleItem) => a.time - b.time,
          ),
          title:
            day === 0
              ? i18n.t('shared:CRUProgramSchedule.first_day_section_header')
              : i18n.t('shared:CRUProgramSchedule.day_section_header', {
                  day: day + 1,
                }),
        });
      });

    return sections;
  }

  addConference(conference: ScheduledConference) {
    this.conferences.push(conference);
  }

  addTask(task: ScheduledTask) {
    this.tasks.push(task);
  }

  async clear() {
    if (!(await alert.confirm())) {
      return;
    }

    runInAction(() => {
      this.conferences = [];
      this.tasks = [];
    });
  }

  async getData() {
    let lastEdition = this.lastEdition;

    lastEdition = {
      ...lastEdition,
      conferences: this.isIndividual
        ? []
        : repeatScheduleEvents(this.conferences, this.programDuration).filter(
            this.isScheduleItemInDurationRange,
          ),
      tasks: this.tasks.filter(this.isScheduleItemInDurationRange),
    } as unknown as Edition;

    let editions = this.program?.editions.slice();

    if (editions?.length) {
      editions.splice(editions.length - 1, 1, lastEdition);
    } else {
      editions = [lastEdition];
    }

    return {
      editions,
    };
  }

  getScheduledItemStartDay(scheduleItem: ScheduleItem) {
    const searchKey =
      scheduleItem.type.id === ProgramScheduleItemTypeEnum.CONFERENCE.id
        ? 'conferences'
        : 'tasks';

    const scheduleItems = this[searchKey].filter(
      item => item.uuid === scheduleItem.uuid,
    );

    const days = scheduleItems.map(item => item.day);

    return days.length ? Math.min(...days) : scheduleItem.day;
  }

  keyExtractor(item: ScheduleItem) {
    return (item as ScheduledConference).edition_uuid || item.uuid;
  }

  removeScheduleItem(item: ScheduleItem) {
    if (item.type.id === ProgramScheduleItemTypeEnum.CONFERENCE.id) {
      this.conferences = this.conferences.filter(
        conference => conference.uuid !== item.uuid,
      );
    } else {
      const index = this.tasks.findIndex(task => task.uuid === item.uuid);

      if (index >= 0) {
        this.tasks.splice(index, 1);
      }
    }
  }

  setIsIndividual(isIndividual: boolean) {
    this.isIndividual = isIndividual;
  }

  setProgram(program: Program) {
    this.program = program;
  }

  updateScheduleEvent(
    event: ScheduleEvent,
    type:
      | (typeof ProgramScheduleItemTypeEnum)['CONFERENCE']
      | (typeof ProgramScheduleItemTypeEnum)['TASK'],
  ) {
    if (type.id === ProgramScheduleItemTypeEnum.CONFERENCE.id) {
      const index = this.conferences.findIndex(
        _item => _item.uuid === event.uuid,
      );

      if (index >= 0) {
        this.conferences[index] = event as ScheduledConference;
      }
    } else {
      const index = this.tasks.findIndex(_item => _item.uuid === event.uuid);

      if (index >= 0) {
        this.tasks[index] = event as ScheduledTask;
      }
    }
  }

  private isScheduleItemInDurationRange(item: ScheduleEvent) {
    if (this.lastEdition) {
      const maxDay = this.programDuration - 1;

      if (item.day > maxDay) {
        return false;
      }
    }

    return true;
  }
}
